import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Container, Card, TextField, Button, Text, Flex, Checkbox } from '@radix-ui/themes';
import { EnvelopeClosedIcon, LockClosedIcon } from '@radix-ui/react-icons';

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);

  // Carica l'email salvata al mount del componente
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';

    if (savedRememberMe && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    // Pulizia: rimuovi eventuale password salvata in precedenza (sicurezza)
    localStorage.removeItem('rememberedPassword');
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: { user }, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Salva o rimuovi l'email in base allo stato di rememberMe (mai la password)
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberMe');
      }

      // Check if user is admin
      const { data: employee } = await supabase
        .from('employees')
        .select('role')
        .eq('id', user?.id || '')
        .single();

      if (!employee || employee.role !== 'AMM.') {
        throw new Error('Accesso consentito solo agli amministratori');
      }

      window.location.href = '/';
    } catch (error) {
      if (import.meta.env.DEV) console.error('Login error:', error);
      setError(error instanceof Error ? error.message : 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size="1">
      <Flex direction="column" gap="6" justify="center" style={{ minHeight: '100vh' }}>
        <Card size="3" style={{ maxWidth: '400px', margin: '0 auto', width: '100%' }}>
          <Flex direction="column" gap="5" p="6">
            <Flex justify="center" mb="4">
              <img 
                src="https://hrpwacxomgudoclntzzi.supabase.co/storage/v1/object/public/img//logo_crs_full_grigio.svg"
                alt="CRS Logo"
                style={{ 
                  width: '100%', 
                  maxWidth: '200px',
                  height: 'auto'
                }}
              />
            </Flex>

            {error && (
              <Text color="red" size="2" align="center">
                {error}
              </Text>
            )}

            <form onSubmit={handleLogin}>
              <Flex direction="column" gap="4">
                <div>
                  <Text as="label" size="2" mb="1" weight="medium" htmlFor="email">
                    Email
                  </Text>
                  <TextField.Root>
                    <TextField.Slot>
                      <EnvelopeClosedIcon height="16" width="16" />
                    </TextField.Slot>
                    <TextField.Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Inserisci la tua email"
                      required
                      autoComplete="email"
                    />
                  </TextField.Root>
                </div>

                <div>
                  <Text as="label" size="2" mb="1" weight="medium" htmlFor="password">
                    Password
                  </Text>
                  <TextField.Root>
                    <TextField.Slot>
                      <LockClosedIcon height="16" width="16" />
                    </TextField.Slot>
                    <TextField.Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Inserisci la tua password"
                      required
                      autoComplete="current-password"
                    />
                  </TextField.Root>
                </div>

                <Flex gap="2" align="center">
                  <Checkbox
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                    id="rememberMe"
                  />
                  <Text as="label" size="2" htmlFor="rememberMe">
                    Ricorda Email
                  </Text>
                </Flex>

                <Button size="3" disabled={loading}>
                  {loading ? 'Accesso in corso...' : 'Accedi'}
                </Button>
              </Flex>
            </form>
          </Flex>
        </Card>
      </Flex>
    </Container>
  );
}